import React, { createContext, useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const storedCart = localStorage.getItem('cart');
    return storedCart ? JSON.parse(storedCart) : [];
  });
  const [cartLastUpdated, setCartLastUpdated] = useState(Date.now());

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const syncCartPrices = async () => {
    if (cart.length === 0) return;

    const updatedCart = [...cart];
    let hasChanges = false;

    for (let i = 0; i < updatedCart.length; i++) {
      const item = updatedCart[i];

      // Promotion items are not catalogue products - there is no /products/{id} to
      // re-price them against, and their price is re-verified server side at checkout
      if (item.promotionProductId) continue;

      try {
        const response = await fetch(`${API_BASE_URL}/products/${item.productID}`);
        if (response.ok) {
          const productData = await response.json();

          let currentPrice;

          if (item.selectedVariant && item.selectedVariant.variantId) {
            const variant = productData.Variants?.find(
              v => v.VariantId === item.selectedVariant.variantId
            );
            if (variant && variant.Price !== null) {
              currentPrice = variant.Price;
            }
          } else if (productData.Price !== null) {
            currentPrice = productData.Price;
          }

          if (currentPrice !== undefined && Math.abs(item.price - currentPrice) > 0.01) {
            updatedCart[i] = {
              ...item,
              price: currentPrice,
              priceUpdated: true
            };
            hasChanges = true;
          }
        }
      } catch (error) {
        // Silent fail (previously console.error)
      }
    }

    if (hasChanges) {
      setCart(updatedCart);
      setCartLastUpdated(Date.now());
      return true;
    }

    return false;
  };

  useEffect(() => {
    syncCartPrices();

    const syncInterval = setInterval(() => {
      syncCartPrices();
    }, 30 * 60 * 1000);

    return () => clearInterval(syncInterval);
  }, []);

  const addToCart = (product, quantity) => {
    setCart(prevCart => {
      // A promotion item is identified by promotionProductId; a catalogue item by
      // productID plus the chosen variant
      const isSameLine = (item) =>
        product.promotionProductId
          ? item.promotionProductId === product.promotionProductId
          : item.productID === product.productID
            && !item.promotionProductId
            && item.selectedVariant?.variantId === product.selectedVariant?.variantId;

      const existingProduct = prevCart.find(isSameLine);

      if (existingProduct) {
        return prevCart.map(item =>
          item.productID === product.productID && item.selectedVariant?.variantId === product.selectedVariant?.variantId ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        return [...prevCart, {
          productID: product.productID ?? null,
          // Set instead of productID for standalone promotion items
          promotionProductId: product.promotionProductId ?? null,
          imageUrl: product.imageUrl ?? null,
          name: product.name,
          price: product.price,
          quantity: quantity,
          selectedVariant: product.selectedVariant || null,
          isRedeemable: product.isRedeemable || false,
          moq: product.moq || 1,
          isIncreaseByMOQ: product.isIncreaseByMOQ || false,
          uom: product.uom || "",
          addedAt: Date.now()
        }];
      }
    });
    setCartLastUpdated(Date.now());
  };

  // Identifies a single cart line. A promotion item is keyed on promotionProductId -
  // its productID is null, so matching on productID alone would hit every promotion
  // item in the cart at once.
  const matchesLine = (item, productID, variantId, promotionProductId) =>
    promotionProductId
      ? item.promotionProductId === promotionProductId
      : item.productID === productID
        && !item.promotionProductId
        && item.selectedVariant?.variantId === variantId;

  const removeFromCart = (productID, variantId, promotionProductId) => {
    setCart(prevCart => {
      // Promotion items have no variants - remove just this one line
      if (promotionProductId) {
        return prevCart.filter(item => item.promotionProductId !== promotionProductId);
      }

      if (variantId) {
        const updatedCart = prevCart.filter(item => !(item.productID === productID && item.selectedVariant?.variantId === variantId));
        const remainingVariants = updatedCart.filter(item => item.productID === productID);

        if (remainingVariants.length === 0) {
          //If no variants are left, remove all products with the productID
          return updatedCart.filter(item => item.productID !== productID);
        }
        return updatedCart;

      } else {
        // Remove all variants of this catalogue product, leaving promotion items alone
        return prevCart.filter(item => item.promotionProductId || item.productID !== productID);
      }
    });
    setCartLastUpdated(Date.now());
  };

  const decreaseQuantity = (productID, variantId, moqOverride, incByMoqOverride, promotionProductId) => {
    setCart(prevCart => {
      const existingProduct = prevCart.find(item =>
        matchesLine(item, productID, variantId, promotionProductId));

      if (!existingProduct) {
        return prevCart;
      }

      // Prefer overrides (passed from Cart using fresh product details), fall back to stored item
      const moq = moqOverride || existingProduct.moq || 1;
      const isIncreaseByMOQ = incByMoqOverride !== undefined ? incByMoqOverride : existingProduct.isIncreaseByMOQ;
      const step = isIncreaseByMOQ ? moq : 1;
      const minQty = moq;

      if (existingProduct.quantity > minQty) {
        return prevCart.map(item =>
          matchesLine(item, productID, variantId, promotionProductId)
            ? { ...item, quantity: Math.max(minQty, item.quantity - step) }
            : item
        );
      } else {
        return prevCart.filter(item =>
          !matchesLine(item, productID, variantId, promotionProductId));
      }
    });
    setCartLastUpdated(Date.now());
  };

  const updateQuantity = (productID, variantId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productID, variantId);
      return;
    }

    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.productID === productID &&
          item.selectedVariant?.variantId === variantId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
    });
    setCartLastUpdated(Date.now());
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
    setCartLastUpdated(Date.now());
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      decreaseQuantity,
      updateQuantity,
      clearCart,
      syncCartPrices,
      cartLastUpdated
    }}>
      {children}
    </CartContext.Provider>
  );
};